import { ACTIVITIES, activityPath } from "@/lib/activities";
import { EVIDENCE_MAPS } from "@/lib/evidenceMaps";

// Activity states — clear, never gamified.
//   completed  · the coaching loop genuinely reached its confirmation point
//   in-progress· saved work, loop not closed (a draft never counts as completed)
//   here       · the one recommended action right now
//   next       · the step after that
//   anytime    · optional, non-linear
//   todo       · not started
const ANYTIME = new Set(["my-confidence-bank", "test-dont-decide"]);

const hasText = (v) => typeof v === "string" && v.trim().length > 0;

export const isCompleted = (profile, key) => {
  if (!profile) return false;
  if (key === "my-spark-starting-point") return profile.starting_point_confirmed === "yes";
  if (EVIDENCE_MAPS[key]) {
    const confirmed = (profile[EVIDENCE_MAPS[key].mapField]?.interpretations || []).some(i => i.status === "confirmed");
    if (confirmed) return true;
  }
  return (profile.completed_activities || []).includes(key) || (profile.completed_activities || []).includes(key.replace("my-spark-", ""));
};

export const isInProgress = (profile, key) => {
  if (!profile || isCompleted(profile, key)) return false;
  if (key === "my-spark-starting-point") return hasText(profile.starting_point_summary);
  if (key === "career-pulse") return !!profile.career_pulse?.scores && Object.keys(profile.career_pulse.scores).length > 0;
  if (EVIDENCE_MAPS[key]) {
    const c = EVIDENCE_MAPS[key];
    return Object.values(profile[c.evidenceField] || {}).some(hasText) || (profile[c.mapField]?.interpretations || []).length > 0;
  }
  return hasText(profile.activity_notes?.[key]?.draft);
};

export const computeJourney = (profile) => {
  const base = ACTIVITIES.map(a => ({
    ...a,
    state: isCompleted(profile, a.key) ? "completed" : isInProgress(profile, a.key) ? "in-progress" : ANYTIME.has(a.key) ? "anytime" : "todo",
  }));
  const linear = base.filter(a => !ANYTIME.has(a.key));
  const here = linear.find(a => a.state === "in-progress") || linear.find(a => a.state === "todo") || null;
  const idx = (a) => ACTIVITIES.findIndex(x => x.key === a.key);
  const next = here ? linear.find(a => a.state === "todo" && a.key !== here.key && idx(a) > idx(here)) || null : null;
  const items = base.map(a => ({
    ...a,
    state: here && a.key === here.key ? "here" : next && a.key === next.key ? "next" : a.state,
    wasInProgress: a.state === "in-progress",
  }));
  return { items, here: items.find(i => i.state === "here") || null, next: items.find(i => i.state === "next") || null };
};

export const STATE_LABEL = {
  completed: "Completed ✓",
  "in-progress": "In progress",
  here: "You're here",
  next: "Up next",
  anytime: "Available anytime",
  todo: "",
};

// The single dominant action for this moment in the journey.
export const nextAction = (profile) => {
  if (!profile) return null;
  if (profile.c1_active || !profile.onboarded) {
    return { kind: "new", eyebrow: "Begin", headline: "Let's start with what brought you here.", sub: "You don't need to have anything figured out yet.", cta: profile.c1_active && profile.onboarded ? "Continue the conversation" : "Start my Spark journey", to: "/conversation/why-am-i-here" };
  }
  if (profile.starting_point_confirmed !== "yes") {
    return { kind: "partly", eyebrow: "One small step", headline: "Here's what I heard.", sub: "Take a look at your Starting Point and tell me whether it fits — you can correct any of it.", cta: "Review my Starting Point", to: "/starting-point" };
  }
  const { here } = computeJourney(profile);
  if (!here) {
    return { kind: "done", eyebrow: "When you're ready", headline: "Okay. What's next?", sub: "You've worked through Part One. Turn what you've discovered into your next move.", cta: "Make my move", to: "/part-two" };
  }
  if (here.wasInProgress) {
    return { kind: "continue", eyebrow: "Pick up where you left off", headline: `You're currently exploring ${here.focus}.`, sub: here.opening, cta: `Continue my ${here.title}`, to: activityPath(here), activity: here };
  }
  const anyDone = ACTIVITIES.some(a => a.key !== "my-spark-starting-point" && isCompleted(profile, a.key));
  return {
    kind: anyDone ? "next" : "first",
    eyebrow: anyDone ? "Here's what I'd explore next…" : "Where we'll begin",
    headline: here.title,
    sub: here.opening,
    cta: anyDone ? "Continue" : `Begin ${here.title}`,
    to: activityPath(here),
    activity: here,
  };
};
